import React, { useRef, useState } from 'react';
import { Button, Form, Input, List, Modal, Popconfirm, Radio, Space, theme, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Rect, Text } from 'react-konva';
import Konva from 'konva';
import { useTranslation } from 'react-i18next';
import { useObjectsApi } from '../../../api/objects/objects';
import { TGDDesign, TGDZone } from '../../../models/courses/graphicDesign';
import type { TImage } from '../../../models/objects/media';
import GDStage from '../canvas/GDStage';
import LocalizedInput, { TAdminLanguage } from './LocalizedInput';

interface Props { zones: TGDZone[]; onChange: (zones: TGDZone[]) => void;
  artboard: { width: number; height: number }; design: TGDDesign;
  backgroundImage?: string; onBackgroundImage: (url?: string) => void; readOnly?: boolean;
  language: TAdminLanguage }
type Point = { x: number; y: number };
const ZonesEditor: React.FC<Props> = ({ zones, onChange, artboard, design,
  backgroundImage, onBackgroundImage, readOnly, language }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const objectsApi = useObjectsApi();
  const [selected, setSelected] = useState<string | null>(null);
  const [start, setStart] = useState<Point | null>(null);
  const [end, setEnd] = useState<Point | null>(null);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [images, setImages] = useState<TImage[]>([]);
  const [search, setSearch] = useState('');
  const stageRef = useRef<Konva.Stage | null>(null);
  const current = zones.find(zone => zone.id === selected);
  const point = (): Point | null => {
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    if (!stage || !pointer) return null;
    return { x: Math.max(0, Math.min(artboard.width, pointer.x / stage.scaleX())),
      y: Math.max(0, Math.min(artboard.height, pointer.y / stage.scaleY())) };
  };
  const finish = () => {
    const last = point() ?? end;
    if (start && last && !readOnly) {
      const x = Math.min(start.x, last.x);
      const y = Math.min(start.y, last.y);
      const w = Math.abs(last.x - start.x);
      const h = Math.abs(last.y - start.y);
      if (w >= 12 && h >= 12) {
        let number = 1;
        while (zones.some(zone => zone.id === `z${number}`)) number += 1;
        const zone: TGDZone = { id: `z${number}`, x, y, w, h,
          options: [{ fr: '' }, { fr: '' }], correct: 0, lesson: { fr: '' } };
        onChange([...zones, zone]);
        setSelected(zone.id);
      }
    }
    setStart(null);
    setEnd(null);
  };
  const update = (patch: Partial<TGDZone>) => current &&
    onChange(zones.map(zone => zone.id === current.id ? { ...zone, ...patch } : zone));
  const openMedia = () => {
    setMediaOpen(true);
    setMediaLoading(true);
    objectsApi.getImages().then(response => setImages(response.data || []))
      .catch(() => Modal.error({ title: t('graphicDesignLesson.admin.mediaError') }))
      .finally(() => setMediaLoading(false));
  };
  const box = (a: Point, b: Point) => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) });
  return <>
    <Space wrap style={{ marginBottom: 12 }}>
      <Button onClick={openMedia} disabled={readOnly}>{t('graphicDesignLesson.admin.chooseImage')}</Button>
      {backgroundImage && <Button onClick={() => onBackgroundImage(undefined)} disabled={readOnly}>
        {t('graphicDesignLesson.admin.removeImageNew')}</Button>}
      <Typography.Text type="secondary">{t('graphicDesignLesson.admin.drawZoneNew')}</Typography.Text>
    </Space>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 2fr) minmax(260px, 1fr)',
      gap: 16 }}>
      <div className="gd-stage-frame" style={{ maxWidth: 720,
        aspectRatio: `${artboard.width} / ${artboard.height}` }}><GDStage design={design} artboard={artboard}
        selectedId={null} readOnly zoom="fit" direction={i18n.dir() as 'ltr' | 'rtl'}
        onSelect={() => undefined} onPreview={() => undefined} onCommit={() => undefined}
        onStageReady={stage => { stageRef.current = stage; }} backgroundImage={backgroundImage}
        overlay={<>
          <Rect width={artboard.width} height={artboard.height} fill="rgba(0,0,0,0)"
            onMouseDown={() => setStart(point())} onTouchStart={() => setStart(point())}
            onMouseMove={() => { if (start) setEnd(point()); }}
            onTouchMove={() => { if (start) setEnd(point()); }}
            onMouseUp={finish} onTouchEnd={finish} />
          {zones.map((zone, index) => <React.Fragment key={zone.id}>
            <Rect x={zone.x} y={zone.y} width={zone.w} height={zone.h}
              stroke={zone.id === selected ? token.colorPrimary : token.colorError}
              strokeWidth={2} onClick={() => setSelected(zone.id)}
              onTap={() => setSelected(zone.id)} />
            <Rect x={zone.x} y={zone.y} width={28} height={28}
              fill={zone.id === selected ? token.colorPrimary : token.colorError}
              listening={false} />
            <Text x={zone.x} y={zone.y + 5} width={28} align="center"
              text={String(index + 1)} fontSize={18} fontStyle="bold"
              fill={token.colorWhite} listening={false} />
          </React.Fragment>)}
          {start && end && <Rect {...box(start, end)} stroke={token.colorPrimary} dash={[5, 4]}
            listening={false} />}
        </>} /></div>
      <div>{zones.length ? <List size="small" dataSource={zones} rowKey="id"
        renderItem={zone => <List.Item style={{ background: zone.id === selected
          ? token.colorPrimaryBg : undefined }}><Button type="link"
          onClick={() => setSelected(zone.id)}>
          {t('graphicDesignLesson.admin.zoneNumber', { n: zones.indexOf(zone) + 1 })} · {' '}
          {zone.lesson[language] || zone.lesson.fr || t('graphicDesignLesson.admin.toComplete')}
        </Button></List.Item>} /> : <div style={{ padding: 16,
          background: token.colorFillQuaternary }}><Typography.Text type="secondary">
          {t('graphicDesignLesson.admin.drawZoneNew')}</Typography.Text></div>}
        {current && <Form layout="vertical">
          <h4>{t('graphicDesignLesson.admin.zoneNumber', { n: zones.indexOf(current) + 1 })}</h4>
          <Form.Item label={t('graphicDesignLesson.admin.proposedAnswers')}>
            {current.options.map((option, index) => <div className="gd-admin-row" key={index}>
              <Radio aria-label={t('graphicDesignLesson.admin.correctAnswer')}
                checked={current.correct === index} disabled={readOnly}
                onChange={() => update({ correct: index })} />
              <div style={{ flex: 1 }}><LocalizedInput value={option} language={language}
                readOnly={readOnly} onChange={next => update({ options: current.options.map(
                  (old, i) => i === index ? next : old) })} /></div>
              <Button type="text" icon={<DeleteOutlined />} aria-label={t('graphicDesignLesson.admin.delete')}
                disabled={readOnly || current.options.length <= 2}
                onClick={() => update({ options: current.options.filter((_, i) => i !== index),
                  correct: current.correct === index ? 0 : Math.max(0,
                    current.correct - Number(index < current.correct)) })} />
            </div>)}
            <Button type="dashed" block disabled={readOnly || current.options.length >= 4}
              onClick={() => update({ options: [...current.options, { fr: '' }] })}>
              {t('graphicDesignLesson.admin.addAnswer')}</Button>
          </Form.Item>
          <Form.Item label={t('graphicDesignLesson.admin.lessonNew')}>
            <LocalizedInput value={current.lesson} language={language} multiline readOnly={readOnly}
              onChange={lesson => update({ lesson })} /></Form.Item>
          <Popconfirm title={t('graphicDesignLesson.admin.deleteZoneConfirm')}
            onConfirm={() => { onChange(zones.filter(zone => zone.id !== current.id));
              setSelected(null); }}><Button danger disabled={readOnly}>
              {t('graphicDesignLesson.admin.deleteZone')}</Button></Popconfirm>
        </Form>}
      </div>
    </div>
    <Modal open={mediaOpen} footer={null} width={720}
      title={t('graphicDesignLesson.admin.media')} onCancel={() => setMediaOpen(false)}>
      <Input.Search value={search} onChange={event => setSearch(event.target.value)}
        placeholder={t('graphicDesignLesson.admin.searchMedia')} allowClear />
      <List loading={mediaLoading} rowKey={item => String(item.id)}
        dataSource={images.filter(item => item.file_name.toLowerCase()
          .includes(search.toLowerCase()))}
        renderItem={item => <List.Item actions={[<Button key="select" onClick={() => {
          onBackgroundImage(item.file_path); setMediaOpen(false);
        }}>{t('graphicDesignLesson.admin.select')}</Button>]}>
          <List.Item.Meta avatar={<img src={item.file_path} alt={item.file_name}
            style={{ width: 60, height: 60, objectFit: 'contain' }} />}
            title={item.file_name} description={item.file_path} /></List.Item>} />
    </Modal>
  </>;
};
export default ZonesEditor;
